import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaChevronRight, FaChevronLeft, FaCheckCircle,
  FaBriefcase, FaHeart, FaBullseye, FaRobot, FaUpload,
  FaGlobe, FaFileAlt, FaRocket, FaSpinner, FaInfoCircle,
  FaGraduationCap, FaStethoscope, FaBuilding, FaShoppingCart, FaDatabase,
  FaPlus, FaTrash, FaEdit, FaEye, FaDownload, FaSearch, FaCopy, FaMagic,
  FaLayerGroup, FaNetworkWired, FaBolt, FaTerminal, FaShieldAlt, FaComments, FaChartLine,
  FaHome
} from "react-icons/fa";
import { getApiBase } from "../apiBase";

const InfoTooltip = ({ text }) => (
  <span className="relative inline-block ml-2 group align-middle">
    <span className="cursor-pointer text-gray-400 hover:text-[#5B3A60] text-xs font-bold border border-gray-300 rounded-full w-4 h-4 inline-flex items-center justify-center transition-all group-hover:scale-110 group-hover:border-[#5B3A60]">i</span>
    <div className="absolute left-1/2 -translate-x-1/2 top-6 w-72 px-4 py-3 text-xs leading-relaxed text-gray-700 bg-white border border-gray-100 rounded-xl shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50 transform group-hover:-translate-y-2">
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 transform rotate-45"></div>
      {text}
    </div>
  </span>
);

const MovingDots = () => {
  const [dots, setDots] = useState(".");
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? "." : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);
  return <span className="text-green-400 text-lg font-bold tracking-wider">{dots}</span>;
};

const StatusLog = ({ status, messages }) => (
  <div className="bg-gray-900 rounded-xl p-4 font-mono text-xs text-green-400 shadow-inner overflow-hidden relative border border-gray-800">
    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-500 to-transparent opacity-50"></div>
    <div className="flex items-center gap-2 mb-2 text-gray-400 border-b border-gray-700 pb-2">
      <FaTerminal className="text-green-500" />
      <span>System Log</span>
      <span className="ml-auto flex items-center gap-1 text-[10px]"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span> Live</span>
    </div>
    <div className="space-y-1 h-40 overflow-y-auto custom-scrollbar">
      {messages.length === 0 ? (
        <div className="text-gray-600 italic">Waiting for system events...</div>
      ) : (
        messages.map((msg, idx) => (
          <div key={idx} className="flex items-start gap-2 animate-fadeIn">
            <span className="text-gray-600 whitespace-nowrap">[{msg.time.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}]</span>
            <span className={msg.type === 'error' ? 'text-red-400' : 'text-gray-300'}>{msg.text}</span>
          </div>
        ))
      )}
      {status === "processing" && (
        <div className="flex items-center gap-1 text-green-400 mt-1">
          <span>Processing</span>
          <MovingDots />
        </div>
      )}
    </div>
  </div>
);

const INDUSTRY_TEMPLATES = {
  educationalConsultancy: {
    label: "Education & Student Services",
    icon: <FaGraduationCap className="text-2xl" />,
    domain: "Education & Student Services",
    persona: "Warm, encouraging, and clear. Simplify complex academic paths for students and parents.",
    description: "Help students and learners explore educational options, understand programs, and make informed decisions about their academic or career paths. Provide clear explanations, compare choices fairly, and guide users step-by-step while encouraging curiosity and confidence. Capture inquiries when users seek personalized guidance or counseling.",
    background: [
      "Never guarantee admission.",
      "Verify facts with official sources.",
      "Suggest multiple options, not just one.",
      "Consider financial constraints.",
      "Protect student data strictly."
    ]
  },
  healthcare: {
    label: "Healthcare & Clinics",
    icon: <FaStethoscope className="text-2xl" />,
    domain: "Healthcare & Clinics support",
    persona: "Compassionate, professional, and cautious. Use non-alarming language.",
    description: "Provide accurate and easy-to-understand information about services, treatments, and clinic processes while supporting patients with clear and reassuring communication. Assist with general inquiries and appointment guidance, and direct users to qualified professionals for medical advice or emergencies.",
    background: [
      "NEVER diagnose or prescribe treatment.",
      "Maintain strict HIPAA/patient confidentiality.",
      "Always include medical disclaimers.",
      "Encourage professional check-ups.",
      "Protect patient privacy and data.",
      "Never offer diagnostic services.",
      "Never offer treatment services."
    ]
  },
  realestate: {
    label: "Real Estate & Property Services",
    icon: <FaHome className="text-2xl" />,
    domain: "Real Estate & Property Support",
    persona: "Knowledgeable, honest, and enthusiastic. Explain terms simply.",
    description: "Assist visitors in understanding property options, pricing, locations, and buying or renting processes. Ask relevant questions to understand user needs, present suitable options clearly, and guide users toward the next step such as scheduling visits or connecting with an advisor.",
    background: [
      "Avoid financial guarantees.",
      "Clarify variability (pricing, availability)",
      "Present property information transparently and avoid making promises regarding investment outcomes or availability.",
      "Protect client privacy and data."
    ]
  },
  business: {
    label: "Business & SaaS Support",
    icon: <FaBuilding className="text-2xl" />,
    domain: "Business & SaaS product Support",
    persona: "Professional, secure, and precise. Ensure clarity in all product terms.",
    description: "Help users understand products, features, and solutions by providing clear, accurate, and structured explanations. Support decision-making by answering technical questions, clarifying use cases, and guiding users toward demos, onboarding resources, or support when needed.",
    background: [
      "Prefer clarity over persuasion.",
      "Avoid exaggerating capabilities.",
      "Explain features accurately and avoid overstating product functionality.",
      "Protect client privacy and data."
    ]
  },
  finance: {
    label: "Financial & Insurance Services",
    icon: <FaShoppingCart className="text-2xl" />,
    domain: "Financial & Insurance support",
    persona: "Friendly, helpful, and solution-focused. Prioritize quick resolution.",
    description: "Explain financial products, policies, and services in a clear and trustworthy manner. Help users understand options, benefits, and processes without providing financial advice, and guide them toward professional consultation when decisions require expert evaluation.",
    background: [
      "No investment advice.",
      "Neutral tone.",
      "Explain financial concepts and products without recommending specific financial decisions.",
      "Protect client privacy and data.",
      "Never offer financial advice.",
    ]
  },
  internalKnowledgeBase: {
    label: "Internal Knowledge & support",
    icon: <FaBuilding className="text-2xl" />,
    domain: "Internal Knowledge & support",
    persona: "Professional, secure, and precise. Ensure clarity in all terms.",
    description: "Help employees quickly access accurate company knowledge, policies, and operational guidance. Deliver concise and reliable answers that improve productivity, reduce repetitive questions, and direct users to the appropriate internal support channels when action is required.",
    background: [
      "Efficiency > conversation warmth.",
      "Encourage ticket escalation quickly.",
      "Provide direct answers and suggest creating a support request when issues require action.",
      "Protect employee privacy and data.",
      "Never offer financial advice.",
      "Never offer insurance services.",
      "Never offer investment services.",
    ]
  },
};

const PERSONA_TEMPLATES = [
  { label: "Professional & Formal", icon: "👔", value: "Maintain a professional, concise, and authoritative tone. Use formal language and structured responses. Avoid slang, humor, or emojis. Focus on clarity, accuracy, and credibility while communicating information objectively." },
  { label: "Friendly & Empathetic", icon: "😊", value: "Communicate in a warm, supportive, and conversational manner. Show understanding of user needs and concerns. Use encouraging and easy-to-understand language, and maintain a welcoming tone. Emojis may be used sparingly when appropriate to convey friendliness." },
  { label: "Clear & Technical", icon: "🔧", value: "Provide clear, accurate, and detail-oriented responses focused on facts and technical correctness. Use precise terminology where helpful, explain concepts logically, and avoid unnecessary conversational filler or emotional language." },
  { label: "Consultative & Persuasive", icon: "🎯", value: "Adopt a helpful and advisory tone that guides users toward informed decisions. Emphasize benefits, clarify options, and encourage next steps naturally without being aggressive or overly promotional." }
];

const MISSION_TEMPLATES = [
  { label: "Customer Support Agent", icon: "🎧", value: "Assist users by answering questions, resolving common issues, and providing accurate information about products or services. Focus on delivering fast, clear solutions while reducing support workload. Escalate complex or sensitive cases to human representatives when necessary." },
  { label: "Lead Generation Agent", icon: "📈", value: "Engage visitors in meaningful conversations to understand their needs, qualify interest, and guide them toward next steps. Collect relevant contact information and help convert inquiries into potential customers without being overly promotional." },
  { label: "Internal Knowledge Base", icon: "📖", value: "Help employees quickly find accurate company information, policies, procedures, and troubleshooting guidance. Provide clear and direct answers to improve productivity and reduce time spent searching across documents or systems." },
  { label: "Educational & Training Assistant", icon: "📚", value: "Support learning by explaining concepts, answering subject-related questions, and guiding users through educational content. Provide clear explanations, examples, and step-by-step understanding while encouraging curiosity and learning progress." }
];

export default function CreateBot() {
  const navigate = useNavigate();
  const tenantId = localStorage.getItem("tenant_id");
  const fileInputRef = useRef(null);
  const uploadPollRef = useRef(null);

  const [step, setStep] = useState(() => {
    try {
      const saved = localStorage.getItem("createBotState");
      if (!saved) return 1;
      const parsed = JSON.parse(saved);
      if (parsed.promptGenerated) return 1;
      return parsed.step || 1;
    } catch {
      return 1;
    }
  });

  const [direction, setDirection] = useState(1);
  const [botForm, setBotForm] = useState({ domain: "", description: "", persona: "", background: "" });
  const [botId, setBotId] = useState(null);
  const [botName, setBotName] = useState("");
  const [loading, setLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [showKBWarning, setShowKBWarning] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [toast, setToast] = useState(null);
  const [showPagesPopup, setShowPagesPopup] = useState(false);
  const [botCreated, setBotCreated] = useState(false);
  const [promptGenerated, setPromptGenerated] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [autoSuggest, setAutoSuggest] = useState(false);
  const [processLogs, setProcessLogs] = useState([]);
  const [processStage, setProcessStage] = useState("");
  const [docSearchTerm, setDocSearchTerm] = useState("");
  const [selectedIndustryKey, setSelectedIndustryKey] = useState(null);
  const [selectedPersonaLabel, setSelectedPersonaLabel] = useState(null);

  const addLog = (text, type = 'info') => {
    setProcessLogs(prev => [...prev, { text, type, time: new Date() }].slice(-20));
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getRecommendedPersonaValue = (industryKey) => {
    switch (industryKey) {
      case "educationalConsultancy":
      case "healthcare":
        return PERSONA_TEMPLATES.find(p => p.label === "Friendly & Empathetic")?.value;
      case "finance":
        return PERSONA_TEMPLATES.find(p => p.label === "Professional & Formal")?.value;
      case "realestate":
        return PERSONA_TEMPLATES.find(p => p.label === "Consultative & Persuasive")?.value;
      case "business":
        return PERSONA_TEMPLATES.find(p => p.label === "Clear & Technical")?.value;
      default:
        return null;
    }
  };

  const handleIndustrySelect = (industry, key) => {
    setSelectedIndustryKey(key);
    const recommendedPersona = getRecommendedPersonaValue(key);
    const matchedPersona = PERSONA_TEMPLATES.find(p => p.value === recommendedPersona);
    if (matchedPersona) setSelectedPersonaLabel(matchedPersona.label);
    setBotForm(prev => ({
      ...prev,
      domain: industry.domain,
      persona: recommendedPersona || prev.persona,
      description: "",
      background: industry.background.join("\n")
    }));
    setBotName(industry.label);
  };

  const getRecommendedMissionValue = (industryKey, personaLabel) => {
    if (!industryKey || !personaLabel) return null;
    if (industryKey === "healthcare") {
      if (personaLabel === "Friendly & Empathetic") return MISSION_TEMPLATES.find(m => m.label === "Customer Support Agent")?.value;
      if (personaLabel === "Professional & Formal") return MISSION_TEMPLATES.find(m => m.label === "Internal Knowledge Base")?.value;
    }
    if (industryKey === "finance") return MISSION_TEMPLATES.find(m => m.label === "Lead Generation Agent")?.value;
    if (industryKey === "educationalConsultancy") return MISSION_TEMPLATES.find(m => m.label === "Educational & Training Assistant")?.value;
    if (industryKey === "realestate") return MISSION_TEMPLATES.find(m => m.label === "Customer Support Agent")?.value;
    if (industryKey === "business") return MISSION_TEMPLATES.find(m => m.label === "Customer Support Agent")?.value;
    return null;
  };

  const recommendedMissionValue =
    selectedIndustryKey && selectedPersonaLabel
      ? getRecommendedMissionValue(selectedIndustryKey, selectedPersonaLabel)
      : null;

  const handlePersonaSelect = (value, label) => {
    setSelectedPersonaLabel(label);
    setBotForm(prev => ({ ...prev, persona: value }));
  };

  const handleMissionSelect = (value) => {
    setBotForm(prev => ({ ...prev, description: value }));
  };

  const nextStep = () => {
    if (step === 2) {
      if (selectedIndustryKey && botForm.persona) {
        const personaLabel = PERSONA_TEMPLATES.find(p => p.value === botForm.persona)?.label;
        const recommendedMission = getRecommendedMissionValue(selectedIndustryKey, personaLabel);
        if (recommendedMission) {
          setBotForm(prev => ({ ...prev, description: recommendedMission }));
        }
      }
    }
    if (step < 7) {
      setDirection(1);
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const createBot = async () => {
    if (!botForm.domain.trim() || !botForm.description.trim() || !botForm.persona.trim()) {
      showToast("Please complete all required fields", "error");
      return;
    }
    try {
      setLoading(true);
      setProcessStage("Creating Agent Identity...");
      addLog("Initializing Agent creation sequence...");
      const payload = { ...botForm, bot_name: botName || "My Assistant" };
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error("Failed to create bot");
      setBotId(data.bot_id);
      setBotCreated(true);
      addLog(`Agent ID ${data.bot_id} created successfully.`);
      showToast("Agent created successfully!");
      setTimeout(() => {
        setLoading(false);
        nextStep();
      }, 1500);
    } catch (err) {
      console.error(err);
      addLog("Error creating Agent: " + err.message, 'error');
      showToast("Failed to create Agent. Please try again.", "error");
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!botId) return;
    try {
      const res = await fetch(`${getApiBase()}/knowledge-base?tenant_id=${tenantId}&bot_id=${botId}`);
      const data = await res.json();
      const normalized = (data.files || []).map(doc => ({
        file_id: doc.file_id,
        file_name: doc.file_name,
        file_type: doc.file_type,
        status: doc.status === "completed" ? "active" : doc.status,
        uploaded_at: doc.created_at
      }));
      setDocuments(normalized);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  };

  useEffect(() => {
    if (botCreated && botId) fetchDocuments();
  }, [botCreated, botId]);

  useEffect(() => {
    const savedData = localStorage.getItem("createBotState");
    if (!savedData) return;
    try {
      const parsed = JSON.parse(savedData);
      if (parsed.promptGenerated === true) {
        localStorage.removeItem("createBotState");
        return;
      }
      setBotId(parsed.botId ?? null);
      setBotCreated(parsed.botCreated ?? false);
      setPromptGenerated(parsed.promptGenerated ?? false);
      setProcessStage(parsed.processStage ?? "");
      if (parsed.botForm) setBotForm(parsed.botForm);
      if (parsed.botName) setBotName(parsed.botName);
      if (parsed.selectedIndustryKey) setSelectedIndustryKey(parsed.selectedIndustryKey);
      if (parsed.selectedPersonaLabel) setSelectedPersonaLabel(parsed.selectedPersonaLabel);
    } catch (err) {
      console.error("Failed to restore state:", err);
      localStorage.removeItem("createBotState");
    }
  }, []);

  useEffect(() => {
    if (promptGenerated) {
      localStorage.removeItem("createBotState");
      return;
    }
    const stateToSave = {
      step,
      botId,
      botCreated,
      promptGenerated,
      botForm,
      botName,
      selectedIndustryKey,
      selectedPersonaLabel,
    };
    localStorage.setItem("createBotState", JSON.stringify(stateToSave));
  }, [step, botId, botCreated, promptGenerated, botForm, botName, selectedIndustryKey, selectedPersonaLabel]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (processStage === "Activating Intelligence...") {
        e.preventDefault();
        e.returnValue = "Activation is in progress. Are you sure you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [processStage]);

  const fetchPages = async () => {
    if (!websiteUrl.trim()) { showToast("Enter website URL", "error"); return; }
    setLoadingPages(true);
    addLog(`Connecting to ${websiteUrl}...`);
    try {
      const res = await fetch(`${getApiBase()}/sitemap-pages?tenant_id=${tenantId}&bot_id=${botId}&url=${encodeURIComponent(websiteUrl)}`);
      const data = await res.json();
      setPages(data.pages || []);
      setSelectedPages([]);
      setShowPagesPopup(true);
      addLog(`Found ${data.pages?.length || 0} potential pages.`);
    } catch {
      showToast("Failed to fetch pages", "error");
      addLog("Failed to retrieve sitemap.", 'error');
    } finally {
      setLoadingPages(false);
    }
  };

  const scrapeSelected = async () => {
    if (selectedPages.length === 0) { showToast("Select at least one page", "error"); return; }
    setScraping(true);
    setProcessStage("Scraping Content...");
    addLog(`Initiating scrape for ${selectedPages.length} pages...`);
    const initialDocCount = documents.length;
    try {
      await fetch(`${getApiBase()}/scrape-selected-pages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId, bot_id: botId, pages: selectedPages })
      });
      showToast("Scraping started...");
      waitForScraping(initialDocCount);
    } catch {
      showToast("Scraping failed", "error");
      setScraping(false);
      addLog("Scraping process failed.", 'error');
    }
  };

  const waitForScraping = (initialDocCount) => {
    const expectedNewDocs = selectedPages.length;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${getApiBase()}/knowledge-base?tenant_id=${tenantId}&bot_id=${botId}`);
        const data = await res.json();
        const files = (data.files || []).map(doc => ({
          ...doc,
          uploaded_at: doc.uploaded_at || doc.created_at || new Date().toISOString(),
          status: doc.status === "completed" ? "active" : doc.status
        }));
        setDocuments(files);
        const newDocsCount = files.length - initialDocCount;
        const stillProcessing = files.some(doc => doc.status === "processing" || doc.status === "queued");
        if (newDocsCount >= expectedNewDocs && !stillProcessing) {
          clearInterval(interval);
          setScraping(false);
          setShowPagesPopup(false);
          setPages([]);
          setSelectedPages([]);
          setWebsiteUrl("");
          addLog("Scraping completed successfully.");
          showToast("Scraping completed");
          setProcessStage("");
        }
      } catch (err) {
        console.error("Polling failed", err);
      }
    }, 3000);
  };

  const toggleSelectAllPopup = () => {
    if (selectedPages.length === pages.length) setSelectedPages([]);
    else setSelectedPages(pages);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !botId) return;
    setUploading(true);
    setProcessStage("Uploading & Processing...");
    addLog(`Uploading ${file.name}...`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${getApiBase()}/documents/ingest?tenant_id=${tenantId}&bot_id=${botId}`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.detail || "Upload failed", "error");
        addLog("Upload failed: " + (data.detail || "Unknown error"), 'error');
        setUploading(false);
        return;
      }
      showToast("Upload started");
      addLog("File received. Starting ingestion pipeline...");
      uploadPollRef.current = setInterval(async () => {
        addLog("Checking ingestion status...");
        const res = await fetch(`${getApiBase()}/knowledge-base?tenant_id=${tenantId}&bot_id=${botId}`);
        const data = await res.json();
        const files = (data.files || []).map(doc => ({
          ...doc,
          uploaded_at: doc.uploaded_at || doc.created_at || new Date().toISOString(),
          status: doc.status === "completed" ? "active" : doc.status
        }));
        setDocuments(files);
        const stillProcessing = files.some(doc => doc.status === "processing" || doc.status === "queued");
        if (!stillProcessing) {
          clearInterval(uploadPollRef.current);
          uploadPollRef.current = null;
          setUploading(false);
          addLog("Ingestion complete. Document active.");
          showToast("Upload completed");
          setProcessStage("");
        }
      }, 2000);
    } catch (err) {
      showToast("Upload failed", "error");
      addLog("Upload error: " + err.message, 'error');
      setUploading(false);
    }
  };

  const handleActivateClick = () => {
    if (loading) return;
    const hasActiveDocs = documents.some(doc => doc.status === "active");
    if (!hasActiveDocs) {
      setShowKBWarning(true);
      return;
    }
    handleGeneratePrompts();
  };

  const handleGeneratePrompts = async () => {
    if (!botId) { showToast("Bot not created yet", "error"); return; }
    try {
      setLoading(true);
      setProcessStage("Activating Intelligence...");
      setProcessLogs([]);
      addLog("Analyzing agent identity, mission parameters, and behavioral rules...");
      setTimeout(() => addLog("Calibrating communication style to match industry context and voice profile..."), 800);
      setTimeout(() => addLog("Indexing and integrating uploaded knowledge sources..."), 1600);
      setTimeout(() => addLog("Interpreting domain context and aligning with operational intent..."), 2400);
      setTimeout(() => addLog("Validating provided data and resolving semantic relationships..."), 3200);
      setTimeout(() => addLog("Constructing agent reasoning flows and interaction scripts..."), 4000);
      setTimeout(() => addLog("Finalizing cognitive model… preparing activation"), 4800);
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/generate-prompts`, { method: "POST" });
      if (!res.ok) throw new Error("Failed");
      addLog("Your assistant is ready to start conversations");
      showToast("Prompt generated successfully!");
      setPromptGenerated(true);
      localStorage.removeItem("createBotState");
      setTimeout(() => navigate(`/dashboard/${tenantId}`), 3000);
    } catch {
      showToast("Prompt generation failed", "error");
      addLog("Activation failed.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStepProgress = () => `${((step - 1) / 6) * 100}%`;

  const steps = [
    { num: 1, title: "Industry", icon: FaBriefcase },
    { num: 2, title: "Personality", icon: FaHeart },
    { num: 3, title: "Mission", icon: FaBullseye },
    { num: 4, title: "Rules", icon: FaRobot },
    { num: 5, title: "Upload", icon: FaUpload },
    { num: 6, title: "Scrape", icon: FaGlobe },
    { num: 7, title: "Activate", icon: FaRocket }
  ];

  const canProceed = () => {
    switch (step) {
      case 1: return botForm.domain.trim();
      case 2: return botForm.persona.trim();
      case 3: return botForm.description.trim();
      case 4: return true;
      case 5: return true;
      case 6: return true;
      case 7: return botCreated;
      default: return false;
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    showToast("Copied to clipboard!");
  };

  const autoFillSuggestions = async (field) => {
    try {
      setLoading(true);
      setProcessStage("Enhancing Content...");
      addLog("Sending data to AI model...");
      let stepNumber = 1;
      if (field === "domain") stepNumber = 1;
      if (field === "persona") stepNumber = 2;
      if (field === "description") stepNumber = 3;
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/enhance-expand`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step: stepNumber, domain: botForm.domain, persona: botForm.persona, description: botForm.description }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Enhancement failed");
      addLog("AI enhancement completed.");
      if (field === "domain") setBotForm(prev => ({ ...prev, domain: data.enhanced_text }));
      if (field === "persona") setBotForm(prev => ({ ...prev, persona: data.enhanced_text }));
      if (field === "description") setBotForm(prev => ({ ...prev, description: data.enhanced_text }));
      showToast("Enhanced successfully!");
    } catch (err) {
      addLog("Enhancement failed: " + err.message, "error");
      showToast("Enhancement failed", "error");
    } finally {
      setLoading(false);
      setProcessStage("");
    }
  };

  const filteredIndustries = Object.entries(INDUSTRY_TEMPLATES).filter(([key, industry]) =>
    industry.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    industry.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDocuments = documents.filter(doc =>
    (doc.file_name || "").toLowerCase().includes(docSearchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans selection:bg-[#5B3A60] selection:text-white">
      <style>{`
        @keyframes slideInRight { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 0 0 rgba(91, 58, 96, 0.4); } 50% { box-shadow: 0 0 0 15px rgba(91, 58, 96, 0); } }
        @keyframes checkmark { 0% { transform: scale(0); } 50% { transform: scale(1.4); } 100% { transform: scale(1); } }
        @keyframes success-pop { 0% { transform: scale(0.8); opacity: 0; } 50% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes shimmer { 0% { background-position: -1000px 0; } 100% { background-position: 1000px 0; } }
        .animate-slideInRight { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-slideInLeft { animation: slideInLeft 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-slideInUp { animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .animate-pulse-glow { animation: pulse-glow 2s infinite; }
        .animate-checkmark { animation: checkmark 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-success-pop { animation: success-pop 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-shimmer { animation: shimmer 2s infinite linear; background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%); background-size: 1000px 100%; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #1a1a1a; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }
      `}</style>

      {toast && (
        <div className={`fixed top-6 right-6 px-6 py-3 rounded-xl text-sm font-bold text-white shadow-2xl z-50 animate-slideInUp flex items-center gap-3 ${toast.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
          {toast.type === "error" ? <FaInfoCircle className="text-lg" /> : <FaCheckCircle className="text-lg" />}
          {toast.msg}
        </div>
      )}

      {showKBWarning && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-3xl shadow-2xl w-[680px] max-w-[90%] p-8 animate-slideInUp">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shadow-inner">
                <FaInfoCircle className="text-blue-600 text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Activation Warning</h3>
                <p className="text-sm text-gray-500 mt-1">Your agent is not fully prepared yet</p>
              </div>
            </div>
            <div className="border-t border-gray-100 mb-6"></div>
            <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
              <p>Your agent is ready to go, but it doesn't have any content yet.</p>
              <p>You can activate it now and add documents later, but it won't be able to answer questions until you do.</p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-900">
                <span className="font-semibold">Recommendation:</span> Adding at least one document before activating is strongly recommended to ensure your agent can respond accurately and effectively.
              </div>
            </div>
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={() => { setShowKBWarning(false); handleGeneratePrompts(); }}
                disabled={loading}
                className={`text-sm font-semibold px-4 py-2 rounded-lg border transition-all ${loading ? "opacity-50 cursor-not-allowed" : "text-gray-600 hover:bg-gray-100"}`}
              >
                {loading ? <span className="flex items-center gap-2"><FaSpinner className="animate-spin" />Activating...</span> : "Activate without Content"}
              </button>
              <button
                onClick={() => { setShowKBWarning(false); setStep(5); }}
                className="px-5 py-2.5 text-sm font-bold bg-[#5B3A60] text-white rounded-xl hover:scale-[1.03] transition-all shadow-md"
              >
                Add Content
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-6 py-8">
        <div className="text-center mb-8 animate-fadeIn">
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">
            Create <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5B3A60] to-[#7B5A80]">AI Assistant</span>
          </h1>
        </div>

        <div className="mb-8 animate-slideInUp">
          <div className="flex justify-between relative mb-4">
            <div className="absolute top-5 left-0 w-full h-1 bg-gray-200 rounded-full -z-10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-full transition-all duration-700 ease-out" style={{ width: getStepProgress() }} />
            </div>
            {steps.map((s) => {
              const Icon = s.icon;
              const isCompleted = step > s.num;
              const isCurrent = step === s.num;
              return (
                <div key={s.num} className="flex flex-col items-center transition-all duration-300 group">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300 shadow-sm
                    ${isCompleted ? 'border-[#5B3A60] bg-[#5B3A60] text-white scale-110' :
                      isCurrent ? 'border-[#5B3A60] bg-white text-[#5B3A60] scale-125 shadow-lg ring-4 ring-[#5B3A60]/10' :
                        'border-gray-300 bg-white text-gray-400'}`}>
                    {isCompleted ? <FaCheckCircle className="text-sm animate-checkmark" /> : <Icon className="text-sm" />}
                  </div>
                  <span className={`text-xs mt-2 font-bold transition-colors duration-300 ${isCurrent ? 'text-[#5B3A60]' : 'text-gray-400'}`}>{s.title}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 bg-white rounded-3xl shadow-xl border border-gray-100 p-8 mb-6 animate-slideInUp overflow-y-auto relative">

          {step === 1 && (
            <div className={`animate-slideInRight ${direction === 1 ? '' : 'animate-slideInLeft'}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center">
                    <FaBriefcase className="text-[#5B3A60] text-xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Select Industry</h2>
                    <p className="text-sm text-gray-500">Choose a template or describe your own</p>
                  </div>
                </div>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input type="text" placeholder="Search industries..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5B3A60] outline-none w-64 shadow-sm transition-all" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {filteredIndustries.map(([key, industry]) => (
                  <button key={key} onClick={() => handleIndustrySelect(industry, key)} className={`p-5 rounded-2xl border text-left transition-all hover:shadow-lg hover:-translate-y-1 group
                    ${botForm.domain === industry.domain ? 'border-[#5B3A60] bg-[#5B3A60]/5 ring-1 ring-[#5B3A60]' : 'border-[#864390] hover:border-[#5B3A60]/50'}`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${botForm.domain === industry.domain ? 'bg-[#5B3A60] text-white' : 'bg-gray-100 group-hover:bg-[#5B3A60] group-hover:text-white'}`}>
                      {industry.icon}
                    </div>
                    <h3 className={`text-sm font-bold mb-2 ${botForm.domain === industry.domain ? 'text-[#5B3A60]' : 'text-gray-900'}`}>{industry.label}</h3>
                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed group-hover:line-clamp-none transition-all duration-300">{industry.description}</p>
                    {botForm.domain === industry.domain && (
                      <div className="mt-3 flex items-center gap-2 text-[#5B3A60] text-xs font-bold bg-white px-2 py-1 rounded-md shadow-sm inline-flex">
                        <FaCheckCircle className="animate-checkmark" /> Selected
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="animate-slideInUp bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Or Describe Manually</label>
                  <button onClick={() => autoFillSuggestions('domain')} className="text-xs text-[#5B3A60] flex items-center gap-1.5 hover:underline font-semibold">
                    <FaMagic className="animate-pulse" /> Auto-suggest
                  </button>
                </div>
                <textarea value={botForm.domain} onChange={(e) => setBotForm({ ...botForm, domain: e.target.value })} placeholder="e.g. Tell me your area of interest..." rows={3} className="w-full border border-gray-200 px-4 py-3 rounded-xl text-sm focus:ring-2 focus:ring-[#5B3A60] outline-none resize-none shadow-sm bg-white" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={`animate-slideInRight ${direction === 1 ? '' : 'animate-slideInLeft'}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center">
                  <FaHeart className="text-[#5B3A60] text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Voice & Personality</h2>
                  <p className="text-sm text-gray-500">How should your Agent communicate?</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {PERSONA_TEMPLATES.map((opt, i) => (
                  <button key={i} onClick={() => handlePersonaSelect(opt.value, opt.label)} className={`relative p-5 rounded-2xl border text-left transition-all hover:shadow-lg hover:-translate-y-1 group
                    ${botForm.persona === opt.value ? 'border-[#5B3A60] bg-[#5B3A60]/5 ring-1 ring-[#5B3A60]' : 'border-gray-100 hover:border-[#5B3A60]/50'}`}>
                    <span className="text-3xl mb-3 block">{opt.icon}</span>
                    <h3 className={`text-sm font-bold mb-2 ${botForm.persona === opt.value ? 'text-[#5B3A60]' : 'text-gray-900'}`}>{opt.label}</h3>
                    <p className="text-xs text-gray-500 line-clamp-3 group-hover:line-clamp-none transition-all duration-300 leading-relaxed">{opt.value}</p>
                    {botForm.persona === opt.value && (
                      <div className="mt-3 flex items-center gap-2 text-[#5B3A60] text-xs font-bold bg-white px-2 py-1 rounded-md shadow-sm inline-flex">
                        <FaCheckCircle className="animate-checkmark" /> Selected
                      </div>
                    )}
                    {selectedIndustryKey && getRecommendedPersonaValue(selectedIndustryKey) === opt.value && (
                      <span className="absolute top-3 right-3 text-[9px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Recommended</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="animate-slideInUp bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Custom Instructions</label>
                  <div className="flex gap-3">
                    <button onClick={() => copyToClipboard(botForm.persona, 'persona')} className="text-xs text-gray-500 flex items-center gap-1.5 hover:text-[#5B3A60] font-semibold">
                      <FaCopy /> {copiedField === 'persona' ? 'Copied!' : 'Copy'}
                    </button>
                    <button onClick={() => autoFillSuggestions('persona')} className="text-xs text-[#5B3A60] flex items-center gap-1.5 hover:underline font-semibold">
                      <FaMagic /> Enhance
                    </button>
                  </div>
                </div>
                <textarea rows={4} value={botForm.persona} onChange={(e) => { setSelectedPersonaLabel(null); setBotForm({ ...botForm, persona: e.target.value }); }} placeholder="Describe tone and style..." className="w-full border border-gray-200 px-4 py-3 rounded-xl text-sm focus:ring-2 focus:ring-[#5B3A60] outline-none resize-none shadow-sm bg-white" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className={`animate-slideInRight ${direction === 1 ? '' : 'animate-slideInLeft'}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center">
                  <FaBullseye className="text-[#5B3A60] text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Core Mission</h2>
                  <p className="text-sm text-gray-500">What is the primary goal?</p>
                </div>
              </div>
              <div className="space-y-3 mb-6">
                {MISSION_TEMPLATES.map((opt, i) => (
                  <button key={i} onClick={() => handleMissionSelect(opt.value)} className={`relative w-full p-4 rounded-xl border text-left transition-all flex items-start gap-4 hover:shadow-md
                    ${botForm.description === opt.value ? 'border-[#5B3A60] bg-[#5B3A60]/5 ring-1 ring-[#5B3A60]' : 'border-gray-100 hover:border-[#5B3A60]/50'}`}>
                    <div className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors
                      ${botForm.description === opt.value ? 'border-[#5B3A60] bg-[#5B3A60]' : 'border-gray-300'}`}>
                      {botForm.description === opt.value && <FaCheckCircle className="text-white text-xs animate-checkmark" />}
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-bold block mb-1 text-gray-900">{opt.icon} {opt.label}</span>
                      <p className="text-xs text-gray-600 leading-relaxed">{opt.value}</p>
                    </div>
                    {recommendedMissionValue === opt.value && (
                      <span className="absolute top-3 right-3 text-[9px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Recommended</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="animate-slideInUp bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Detailed Description</label>
                  <div className="flex gap-3 items-center">
                    <span className={`text-xs font-bold ${limitReached ? 'text-red-500' : 'text-gray-400'}`}>{botForm.description.length}/3000</span>
                    <button onClick={() => autoFillSuggestions('description')} className="text-xs text-[#5B3A60] flex items-center gap-1.5 hover:underline font-semibold">
                      <FaMagic /> Enhance
                    </button>
                  </div>
                </div>
                <textarea rows={5} maxLength={3000} value={botForm.description} onChange={(e) => { const val = e.target.value; setLimitReached(val.length >= 3000); setBotForm({ ...botForm, description: val.slice(0, 3000) }); }} placeholder="Describe bot's purpose..." className={`w-full border px-4 py-3 rounded-xl text-sm focus:ring-2 outline-none resize-none shadow-sm bg-white ${limitReached ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-[#5B3A60]'}`} />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className={`animate-slideInRight ${direction === 1 ? '' : 'animate-slideInLeft'}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center">
                  <FaRobot className="text-[#5B3A60] text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Rules & Intelligence</h2>
                  <p className="text-sm text-gray-500">Add constraints and workflows</p>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-6 flex items-start gap-4">
                <span className="text-2xl">💡</span>
                <div>
                  <h4 className="text-sm font-bold text-blue-900 mb-2">Best Practices</h4>
                  <ul className="text-xs text-blue-800 space-y-1.5">
                    <li className="flex items-center gap-2"><FaCheckCircle className="text-blue-500 text-[10px]" /> Define clear boundaries for your Agent</li>
                    <li className="flex items-center gap-2"><FaCheckCircle className="text-blue-500 text-[10px]" /> Specify escalation paths for complex issues</li>
                    <li className="flex items-center gap-2"><FaCheckCircle className="text-blue-500 text-[10px]" /> Include compliance requirements explicitly</li>
                  </ul>
                </div>
              </div>
              <div className="animate-slideInUp">
                <label className="block text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Additional Rules</label>
                <textarea rows={6} value={botForm.background} onChange={(e) => setBotForm({ ...botForm, background: e.target.value })} placeholder="Example:&#10;- Never provide medical diagnoses&#10;- Verify pricing before quoting&#10;- Maintain data confidentiality" className="w-full border border-gray-200 px-4 py-3 rounded-xl text-sm focus:ring-2 focus:ring-[#5B3A60] outline-none resize-none shadow-sm bg-white font-mono text-gray-700" />
              </div>
            </div>
          )}

          {step === 5 && (
            <div className={`animate-slideInRight ${direction === 1 ? '' : 'animate-slideInLeft'}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center">
                  <FaUpload className="text-[#5B3A60] text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Upload Documents</h2>
                  <p className="text-sm text-gray-500">Add knowledge files</p>
                </div>
              </div>
              {!botCreated ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                  <div className="text-4xl mb-4">📋</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Agent Not Created</h3>
                  <p className="text-sm text-gray-500 mb-6">Complete previous steps first</p>
                  <button onClick={prevStep} className="px-6 py-3 rounded-xl text-sm font-bold bg-[#5B3A60] text-white hover:shadow-lg transition-all">← Back to Setup</button>
                </div>
              ) : (
                <>
                  {processStage && (
                    <div className="mb-6 animate-fadeIn">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-[#5B3A60] uppercase">{processStage}</span>
                        <FaSpinner className="animate-spin text-[#5B3A60]" />
                      </div>
                      <StatusLog status="processing" messages={processLogs} />
                    </div>
                  )}
                  <div onClick={() => !uploading && fileInputRef.current.click()} className={`border-2 border-dotted rounded-2xl px-6 py-12 cursor-pointer transition-all mb-6 text-center group
                    ${uploading ? 'border-[#5B3A60] bg-[#5B3A60]/5' : 'border-gray-300 hover:border-[#5B3A60] hover:bg-gray-50'}`}>
                    <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 transition-colors ${uploading ? 'bg-[#5B3A60] text-white' : 'bg-gray-100 group-hover:bg-[#5B3A60] group-hover:text-white'}`}>
                      <FaUpload className="text-2xl" />
                    </div>
                    <p className="text-sm font-bold text-gray-900 mb-1">Upload Files</p>
                    <p className="text-xs text-gray-500 mb-4">PDF or TXT (max 10MB)</p>
                    <p className={`text-sm font-bold ${uploading ? 'text-[#5B3A60]' : 'text-[#5B3A60] opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                      {uploading ? "⏳ Uploading..." : "📁 Click to upload"}
                    </p>
                    <input ref={fileInputRef} type="file" hidden accept=".pdf,.txt" onChange={handleUpload} />
                  </div>
                  <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="border-b px-4 py-3 bg-gray-50 font-bold text-sm text-gray-700 flex items-center justify-between">
                      <span>Documents ({documents.length})</span>
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input type="text" placeholder="Search..." value={docSearchTerm} onChange={(e) => setDocSearchTerm(e.target.value)} className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs w-40 focus:ring-1 focus:ring-[#5B3A60] outline-none" />
                      </div>
                    </div>
                    {filteredDocuments.length === 0 ? (
                      <div className="py-12 text-center text-gray-400 text-sm flex flex-col items-center">
                        <FaDatabase className="text-3xl mb-2 opacity-50" />
                        No documents yet
                      </div>
                    ) : (
                      <div className="max-h-60 overflow-y-auto custom-scrollbar">
                        {filteredDocuments.map((doc) => (
                          <div key={doc.file_name} className="flex items-center justify-between px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-gray-500"><FaFileAlt /></div>
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-gray-900">{doc.file_name}</span>
                                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">{doc.file_type}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {doc.status === "processing" ? (
                                <span className="px-3 py-1 text-[10px] font-bold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1"><FaSpinner className="animate-spin" /> Processing</span>
                              ) : (
                                <span className="px-3 py-1 text-[10px] font-bold rounded-full bg-green-100 text-green-800 flex items-center gap-1"><FaCheckCircle /> Active</span>
                              )}
                              <span className="text-[10px] text-gray-400">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 6 && (
            <div className={`animate-slideInRight ${direction === 1 ? '' : 'animate-slideInLeft'}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center">
                  <FaGlobe className="text-[#5B3A60] text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Scrape Website</h2>
                  <p className="text-sm text-gray-500">Extract knowledge from URL</p>
                </div>
              </div>
              {!botCreated ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                  <div className="text-4xl mb-4">🌐</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Agent Not Created</h3>
                  <p className="text-sm text-gray-500 mb-6">Complete previous steps first</p>
                  <button onClick={prevStep} className="px-6 py-3 rounded-xl text-sm font-bold bg-[#5B3A60] text-white hover:shadow-lg transition-all">← Back to Setup</button>
                </div>
              ) : (
                <>
                  {processStage && (
                    <div className="mb-6 animate-fadeIn">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-[#5B3A60] uppercase">{processStage}</span>
                        <FaSpinner className="animate-spin text-[#5B3A60]" />
                      </div>
                      <StatusLog status="processing" messages={processLogs} />
                    </div>
                  )}
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl px-6 py-6 mb-6 bg-gray-50">
                    <label className="block text-xs font-bold text-gray-700 mb-2 uppercase">Website URL</label>
                    <input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://yourwebsite.com" className="w-full border border-gray-200 px-4 py-3 rounded-xl text-sm mb-4 focus:ring-2 focus:ring-[#5B3A60] outline-none bg-white shadow-sm" />
                    <button onClick={fetchPages} disabled={loadingPages} className={`w-full py-3 rounded-xl text-sm font-bold transition-all shadow-sm
                      ${loadingPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-white border-2 border-[#5B3A60] text-[#5B3A60] hover:bg-[#5B3A60] hover:text-white'}`}>
                      {loadingPages ? <span className="flex items-center justify-center gap-2"><FaSpinner className="animate-spin" /> Fetching Sitemap...</span> : "🌐 Fetch Pages"}
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="border-b px-4 py-3 bg-gray-50 font-bold text-sm text-gray-700">All Sources ({documents.length})</div>
                    {documents.length === 0 ? (
                      <div className="py-12 text-center text-gray-400 text-sm flex flex-col items-center">
                        <FaNetworkWired className="text-3xl mb-2 opacity-50" />
                        No sources connected yet
                      </div>
                    ) : (
                      <div className="max-h-60 overflow-y-auto custom-scrollbar">
                        {documents.map((doc) => (
                          <div key={doc.file_name} className="flex items-center justify-between px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded bg-blue-50 flex items-center justify-center text-blue-500"><FaGlobe /></div>
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-gray-900">{doc.file_name}</span>
                                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">{doc.file_type}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {doc.status === "processing" ? (
                                <span className="px-3 py-1 text-[10px] font-bold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1"><FaSpinner className="animate-spin" /> Processing</span>
                              ) : (
                                <span className="px-3 py-1 text-[10px] font-bold rounded-full bg-green-100 text-green-800 flex items-center gap-1"><FaCheckCircle /> Active</span>
                              )}
                              <span className="text-[10px] text-gray-400">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 7 && (
            <div className={`${direction === 1 ? 'animate-slideInRight' : 'animate-slideInLeft'}`}>
              {botCreated && !promptGenerated && (
                <div className="mb-6 animate-slideInUp">
                  <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 text-yellow-900 px-6 py-4 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center">
                        <FaInfoCircle className="text-yellow-700 text-sm" />
                      </div>
                      <div>
                        <p className="text-sm font-bold">Your Agent is not activated yet</p>
                        <p className="text-xs text-yellow-800/80">Complete activation to make your assistant start responding.</p>
                      </div>
                    </div>
                    <button
                      onClick={handleActivateClick}
                      disabled={loading}
                      className={`px-6 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all shadow
                        ${loading ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-[#5B3A60] text-white hover:scale-[1.03]"}`}
                    >
                      {loading ? <><FaSpinner className="animate-spin" />Activating...</> : <><FaRocket />Activate Now</>}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5B3A60] to-[#7B5A80] flex items-center justify-center shadow-lg">
                  <FaRocket className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Activate Agent</h2>
                  <p className="text-sm text-gray-500">Review and launch your AI Assistant</p>
                </div>
              </div>

              {!botCreated ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                  <div className="text-4xl mb-4">🚀</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Agent Not Created</h3>
                  <p className="text-sm text-gray-500 mb-6">Complete previous steps first</p>
                  <button onClick={prevStep} className="px-6 py-3 rounded-xl text-sm font-bold bg-[#5B3A60] text-white hover:shadow-lg transition-all">← Back to Setup</button>
                </div>
              ) : (
                <>
                  {processStage && (
                    <div className="mb-6 animate-fadeIn">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-[#5B3A60] uppercase">{processStage}</span>
                        <FaSpinner className="animate-spin text-[#5B3A60]" />
                      </div>
                      <StatusLog status="processing" messages={processLogs} />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="border-l-4 border-[#5B3A60] bg-gray-50 pl-4 py-3 rounded-r-lg">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Identity</p>
                      <p className="text-sm font-bold text-gray-900">Ready</p>
                      <div className="flex items-center gap-1.5 text-green-600 text-[10px] font-bold mt-1"><FaCheckCircle /> Complete</div>
                    </div>
                    <div className="border-l-4 border-[#5B3A60] bg-gray-50 pl-4 py-3 rounded-r-lg">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Knowledge</p>
                      <p className="text-sm font-bold text-gray-900">{documents.length} Sources</p>
                      <div className={`flex items-center gap-1.5 text-[10px] font-bold mt-1 ${documents.length > 0 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {documents.length > 0 ? <><FaCheckCircle /> Ready</> : <><FaInfoCircle /> Optional</>}
                      </div>
                    </div>
                    <div className="border-l-4 border-[#5B3A60] bg-gray-50 pl-4 py-3 rounded-r-lg">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Activation</p>
                      <p className="text-sm font-bold text-gray-900">{promptGenerated ? 'Live' : 'Pending'}</p>
                      <div className={`flex items-center gap-1.5 text-[10px] font-bold mt-1 ${promptGenerated ? 'text-green-600' : 'text-[#5B3A60]'}`}>
                        {promptGenerated ? <><FaCheckCircle /> Live</> : <><FaSpinner className="animate-spin" /> Ready</>}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm animate-slideInUp">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <FaLayerGroup className="text-[#5B3A60]" /> Final Configuration Preview
                      </h3>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
                        <FaCheckCircle /> Ready to Launch
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <FaBriefcase className="text-[#5B3A60]" />
                            <span className="text-xs font-bold text-gray-500 uppercase">Industry & Domain</span>
                          </div>
                          <p className="text-sm font-bold text-gray-900">{botForm.domain || 'Not specified'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <FaHeart className="text-[#5B3A60]" />
                            <span className="text-xs font-bold text-gray-500 uppercase">Tone & Persona</span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{botForm.persona || 'Not specified'}</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 h-full">
                          <div className="flex items-center gap-2 mb-2">
                            <FaBullseye className="text-[#5B3A60]" />
                            <span className="text-xs font-bold text-gray-500 uppercase">Mission & Description</span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed max-h-32 overflow-y-auto custom-scrollbar">{botForm.description || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <FaDatabase className="text-blue-600" />
                          <span className="text-xs font-bold text-blue-800 uppercase">Knowledge Base Count</span>
                        </div>
                        <div className="flex items-end gap-2">
                          <span className="text-2xl font-bold text-blue-900">{documents.length}</span>
                          <span className="text-xs text-blue-600 mb-1">documents indexed</span>
                        </div>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <FaRobot className="text-purple-600" />
                          <span className="text-xs font-bold text-purple-800 uppercase">Safety Rules</span>
                        </div>
                        <p className="text-xs text-purple-700 line-clamp-2">{botForm.background ? botForm.background.split('\n').length + ' constraints defined' : 'No additional constraints'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-2xl p-8 text-center shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    {promptGenerated ? (
                      <div className="animate-success-pop relative z-10">
                        <div className="text-5xl mb-4">🎉</div>
                        <h3 className="text-xl font-bold text-white mb-2">Agent Activated!</h3>
                        <p className="text-sm text-white/80">Redirecting to dashboard...</p>
                      </div>
                    ) : (
                      <div className="relative z-10 text-center">
                        <h3 className="text-xl font-bold text-white mb-2">Ready to Launch?</h3>
                        <p className="text-sm text-white/80 mb-6">Generate prompts to activate your Agent intelligence with all configured settings</p>
                        <div className="flex justify-center">
                          <button
                            onClick={handleActivateClick}
                            disabled={loading}
                            className={`px-6 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all shadow
                              ${loading ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-white text-[#5B3A60] hover:scale-[1.03]"}`}
                          >
                            {loading ? <><FaSpinner className="animate-spin" />Activating...</> : <><FaRocket />Activate Now</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {processStage !== "Activating Intelligence..." && (
          <div className="flex justify-between items-center pb-2">
            <button
              onClick={step === 1 ? () => navigate(`/dashboard/${tenantId}`) : prevStep}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-100 transition-all"
            >
              {step === 1 ? "✕ Cancel" : <><FaChevronLeft size={14} /> Back</>}
            </button>

            {step < 4 ? (
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className="flex items-center gap-2 bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white px-8 py-3 rounded-xl text-sm font-bold hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50"
              >
                Next <FaChevronRight size={14} />
              </button>
            ) : step === 4 ? (
              <button
                onClick={createBot}
                disabled={loading || !canProceed()}
                className="flex items-center gap-2 bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white px-8 py-3 rounded-xl text-sm font-bold hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50"
              >
                {loading ? <><FaSpinner className="animate-spin" /> Creating...</> : <><FaRocket size={14} /> Create Agent</>}
              </button>
            ) : step < 7 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white px-8 py-3 rounded-xl text-sm font-bold hover:shadow-xl hover:scale-105 transition-all"
              >
                Next <FaChevronRight size={14} />
              </button>
            ) : null}
          </div>
        )}
      </div>

      {showPagesPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white w-[600px] max-h-[85vh] rounded-3xl p-6 shadow-2xl relative animate-slideInUp border border-gray-100">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3 text-gray-900">
              <FaGlobe className="text-[#5B3A60]" /> Select Pages
            </h2>
            <div className="flex justify-between items-center mb-4 bg-gray-50 p-3 rounded-xl">
              <p className="text-xs text-gray-500 font-medium">⏱️ Est: 2-3 min per 10 pages</p>
              <button onClick={toggleSelectAllPopup} className="text-xs font-bold text-[#5B3A60] hover:underline bg-white px-3 py-1.5 rounded-lg border border-[#5B3A60]/20">
                {selectedPages.length === pages.length ? "❌ Clear All" : "✅ Select All"}
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-xl p-2 mb-4 space-y-1 custom-scrollbar">
              {pages.map((url) => (
                <label key={url} className="flex items-center gap-3 text-sm p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-transparent hover:border-gray-200">
                  <input type="checkbox" checked={selectedPages.includes(url)} onChange={(e) => { if (e.target.checked) setSelectedPages(prev => [...prev, url]); else setSelectedPages(prev => prev.filter(u => u !== url)); }} className="w-4 h-4 text-[#5B3A60] rounded focus:ring-[#5B3A60]" />
                  <span className="truncate flex-1 text-gray-700">{url}</span>
                </label>
              ))}
            </div>
            {scraping && (
              <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center rounded-3xl z-10">
                <FaSpinner className="w-12 h-12 text-[#5B3A60] animate-spin mb-4" />
                <p className="text-sm font-bold text-[#5B3A60]">Scraping Content...</p>
                <p className="text-xs text-gray-500 mt-1">Please do not close this window</p>
              </div>
            )}
            <div className="flex justify-between items-center pt-4 border-t border-gray-100">
              <span className="text-sm font-bold text-gray-700">{selectedPages.length}/{pages.length} Selected</span>
              <div className="flex gap-3">
                <button onClick={() => setShowPagesPopup(false)} disabled={scraping} className="px-5 py-2.5 text-xs font-bold border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                <button onClick={scrapeSelected} disabled={scraping || selectedPages.length === 0} className="px-6 py-2.5 text-xs font-bold rounded-xl bg-[#5B3A60] text-white hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100">
                  {scraping ? "⏳ Scraping..." : "🚀 Scrape"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}